
import React, { useState } from 'react';
import { useLanguage } from '../../contexts/language';
import { useToast } from '@/hooks/use-toast';
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage,
  FormDescription
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { City } from '../../data/types/city-types';

interface FundingApplicationFormProps {
  city: City;
  onCancel: () => void;
}

const fundingSchema = z.object({
  name: z.string().min(2, { message: 'Name must be at least 2 characters' }),
  email: z.string().email({ message: 'Please enter a valid email' }),
  organization: z.string().min(2, { message: 'Organization name is required' }),
  projectTitle: z.string().min(5, { message: 'Project title must be at least 5 characters' }),
  projectType: z.string().min(1, { message: 'Please select a project type' }),
  requestedAmount: z.coerce.number().min(500, { message: 'Minimum funding request is $500' }),
  projectDescription: z.string().min(100, { message: 'Please provide at least 100 characters' }),
});

const projectTypes = [
  'Church Planting',
  'Building Construction',
  'Community Outreach',
  'Leadership Training',
  'Bible Translation',
  'Media Ministry',
  'Children\'s Ministry',
  'Medical Mission',
  'Education'
];

const FundingApplicationForm: React.FC<FundingApplicationFormProps> = ({ city, onCancel }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<z.infer<typeof fundingSchema>>({
    resolver: zodResolver(fundingSchema),
    defaultValues: {
      name: '',
      email: '',
      organization: '',
      projectTitle: '',
      projectType: '',
      requestedAmount: 5000,
      projectDescription: '',
    },
  });
  
  const handleSubmit = async (values: z.infer<typeof fundingSchema>) => {
    setIsSubmitting(true);
    
    // Simulating submission with a delay
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast({
        title: t('applicationSubmitted'),
        description: t('applicationReviewMessage'),
      });
      
      // Reset form or navigate away
      form.reset();
      onCancel();
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: t('applicationError'),
        description: t('pleaseTryAgain'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="p-4 border rounded-lg bg-gray-50">
      <h3 className="text-lg font-semibold mb-4">{t('applyForFunding')}</h3>
      <p className="text-sm text-gray-600 mb-4">
        {t('applyingForCity', { city: city.name })}
      </p>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('enterName')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('email')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('enterEmail')} type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <FormField
            control={form.control}
            name="organization"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('organization')}</FormLabel>
                <FormControl>
                  <Input placeholder={t('enterOrganization')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="projectTitle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('projectTitle')}</FormLabel>
                <FormControl>
                  <Input placeholder={t('enterProjectTitle')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="projectType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('projectType')}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('selectProjectType')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {projectTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="requestedAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('requestedAmount')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2">$</span>
                      <Input type="number" className="pl-7" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription className="text-xs">
                    {t('minFundingAmount')} $500
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <FormField
            control={form.control}
            name="projectDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('projectDescription')}</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder={t('enterProjectDescription')} 
                    className="min-h-[120px]" 
                    {...field} 
                  />
                </FormControl>
                <FormDescription className="text-xs">
                  {t('minCharCount')}: 100
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div className="flex gap-3 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              className="flex-1"
              onClick={onCancel}
            >
              {t('cancel')}
            </Button>
            <Button 
              type="submit" 
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('submitting') : t('submitApplication')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default FundingApplicationForm;
